import { useEffect, useRef, useState } from 'react'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import styles from './styles'
import { Helmet } from 'react-helmet'
import { handleEditorReady } from '../../utils'
import {
  InlineEditor,
  Bold,
  Italic,
  Essentials,
  Heading,
  CodeBlock,
  List,
  Link,
  Paragraph,
  AccessibilityHelp,
  Autoformat,
  AutoImage,
  BlockQuote,
  RemoveFormat,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageInsert,
  ImageInsertViaUrl,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  HorizontalLine,
  ListProperties,
  PageBreak,
  PictureEditing,
  SelectAll,
  Subscript,
  Superscript,
  Strikethrough,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  TodoList,
  Underline,
  Undo,
  GeneralHtmlSupport,
  Alignment,
  Font,
  HtmlEmbed,
  Autosave,
} from 'ckeditor5'
import 'ckeditor5/ckeditor5.css'
import ENV from 'src/components/editor/config/environment'
import ImageGalleryPlugin from '../custom-plugins/image-gallery'
import VideoGalleryPlugin from '../custom-plugins/video-gallery'
import SimpleBoxToolbar from '../custom-plugins/simple-box-wrapper-options'
import ButtonOptionsPlugin from '../custom-plugins/actions'
import CustomDropdown from '../custom-plugins/ai-asistant'
import { useDispatch } from 'react-redux'
import SimpleBox from '../custom-plugins/simple-box'
import { withStyles } from '@material-ui/core'
import Embed from '../../components/embed'
import CustomVideo from '../custom-plugins/custom-video'
import {
  handleHeaderSettingsModal,
  replacedropAreaWideContent,
} from '../custom-plugins/simple-box/utils'
import InteractionBuilder from '../custom-plugins/interaction-builder'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'

const CKEditorComponent = props => {
  const {
    classes,
    editorRef,
    onDropClose,
    saveData,
    initialData,
    doubleClickData,
    setDoubleClickData,
    fontSize,
    fontFamily,
    setIsOpen,
    language,
    isReadOnly,
    theme,
  } = props
  const [isLayoutReady, setIsLayoutReady] = useState(false)
  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const [localCollectionData, setLocalCollectionData] = useState(null)
  const editorPresenceRef = useRef(null)
  const editorAnnotationsRef = useRef(null)
  const editorRevisionHistoryRef = useRef(null)
  const editorRevisionHistoryEditorRef = useRef(null)
  const editorRevisionHistorySidebarRef = useRef(null)
  const [previousData, setPreviousData] = useState('')
  const editorContainerRef = useRef(null)
  const previousDataRef = useRef(initialData || '')
  const { id, page, subPage } = useParams()
  const open = useSelector(state => state.course.sectionDrawerOpen)
  const courseCards = useSelector(state => state.course.courseCards)
  const course = useSelector(state => state.course.course)
  const pageUrl = window.location.pathname.split('/')[1]
  const courseSettings = useSelector(state => state.course.landingPageSettings)
  const collectionSettings = useSelector(
    state => state.collection.landingPageSettings,
  )
  const landingPageData =
    pageUrl === 'mini-course' ? courseSettings || {} : collectionSettings || {}

  // Get collection data from Redux state
  const collection = useSelector(state => state.collection)

  const dispatch = useDispatch()

  // Update local collection data whenever Redux state changes
  useEffect(() => {
    if (collection) {
      setLocalCollectionData(collection)
    }
  }, [collection])

  useEffect(() => {
    setIsLayoutReady(true)
    return () => setIsLayoutReady(false)
  }, [])

  useEffect(() => {
    addGlobalStyles()
  }, [open, page, subPage])

  // Effect to track when data is loaded
  useEffect(() => {
    if (
      (pageUrl === 'mini-course' && courseSettings) ||
      (pageUrl !== 'mini-course' && collectionSettings)
    ) {
      setIsDataLoaded(true)
    }
  }, [courseSettings, collectionSettings, pageUrl])

  // Create editor configuration when data is loaded
  useEffect(() => {
    if (isDataLoaded || page !== 'landing-page') {
      setIsDataLoaded(true)
    }
  }, [
    isDataLoaded,
    landingPageData,
    courseCards,
    course,
    collection, // This will trigger an update when collection changes
    open,
    page,
    subPage,
  ])

  // Add this global style to your document head once
  const addGlobalStyles = () => {
    const existingStyle = document.getElementById('ck-balloon-panel-style')
    if (existingStyle) {
      existingStyle.remove()
    }

    // Create a new style element with an ID for easy reference
    const styleEl = document.createElement('style')
    styleEl.id = 'ck-balloon-panel-style'

    if (page === 'page' && subPage !== 'editor') {
      if (open) {
        styleEl.textContent = `
          .ck.ck-balloon-panel.ck-balloon-panel_visible {
            padding-left: 280px !important;
          }
        `
      } else {
        styleEl.textContent = `
          .ck.ck-balloon-panel.ck-balloon-panel_visible {
            padding-left: 56px !important;
          }
        `
      }
    } else {
      styleEl.textContent = `
        .ck.ck-balloon-panel.ck-balloon-panel_visible {
          padding-left: 0 !important;
        }
      `
    }

    // Always append the style element to document head
    document.head.appendChild(styleEl)
  }

  const handleEditorChange = (event, editor) => {
    const currentData = editor
      .getData()
      .replace(/<p class="empty-dom">&nbsp;<\/p>/g, '')
      .trim()

    setPreviousData(currentData)
    const rootElement = editor.model.document.getRoot()

    if (currentData === '') {
      editor.model.change(writer => {
        // Clear any existing content
        writer.remove(writer.createRangeIn(rootElement))

        // Check if we're on a landing page
        if (page === 'landing-page') {
          // For landing page, we need to preserve the header and outline structure
          const editorHeader = writer.createElement('editorHeader')
          writer.insert(editorHeader, rootElement, 0)

          // Create and insert the drop area between header and outline
          const dropAreaWide = writer.createElement('dropAreaWide')
          writer.insert(dropAreaWide, rootElement, 1)

          // Add the outline at the end
          const editorOutline = writer.createElement('editorOutline')
          writer.append(editorOutline, rootElement)

          // Set the selection to the drop area
          writer.setSelection(dropAreaWide, 'end')
        } else {
          // Normal behavior for other pages
          const dropAreaWide = writer.createElement('dropAreaWide')
          writer.insert(dropAreaWide, rootElement)
          writer.setSelection(dropAreaWide, 'end')
        }
      })
    }

    editor.model.change(writer => {
      const root = editor.model.document.getRoot()
      const dropAreaNarrows = root._children._nodes.filter(
        child => child.name === 'paragraph',
      )
      dropAreaNarrows.forEach(area => {
        writer.remove(area)
      })
    })
  }

  const editorConfig = {
    toolbar: {
      items: isReadOnly
        ? []
        : [
            'undo',
            'redo',
            'heading',
            '|',
            'bold',
            'italic',
            'alignment',
            'fontColor',
            'link',
            '|',
            'imageGalleryAdd',
            'customVideo',
            '|',
            {
              name: 'listGroup',
              label: 'Show more items',
              items: [
                'fontBackgroundColor',
                'underline',
                'strikethrough',
                'removeFormat',
                'bulletedList',
                'numberedList',
                'horizontalLine',
                'codeBlock',
              ],
            },
            '|',
            'customDropdown',
          ],
      shouldNotGroupWhenFull: true,
    },
    indentBlock: {
      classes: [
        'custom-block-indent-a', // First step - smallest indentation.
        'custom-block-indent-b',
        'custom-block-indent-c', // Last step - biggest indentation.
      ],
    },
    extraPlugins: [
      ImageGalleryPlugin,
      VideoGalleryPlugin,
      CustomDropdown,
      ButtonOptionsPlugin,
    ],
    imageGalleryPlugin: {
      dispatch: dispatch, // pass dispatch here
    },
    videoGalleryPlugin: {
      dispatch: dispatch,
    },
    buttonOptionsPlugin: {
      dispatch: dispatch,
    },
    removePlugins: ['ImageCaption'],
    mediaEmbed: {
      previewsInData: true,
    },
    autosave:
      page !== 'landing-page'
        ? {
            waitingTime: 1000,
            save: async editor => {
              const currentData = editor
                .getData()
                .replace(/<p class="empty-dom">&nbsp;<\/p>/g, '')
                .trim()

              try {
                if (
                  currentData !== previousDataRef.current.trim() &&
                  saveData &&
                  currentData !== ''
                ) {
                  await saveData(currentData)
                  previousDataRef.current = currentData
                }
                return Promise.resolve()
              } catch (error) {
                console.error('Error saving editor data:', error)
                return Promise.reject(error)
              }
            },
          }
        : null,
    ui: {
      viewportOffset: {
        top: 70,
      },
    },

    plugins: [
      Autosave,
      AccessibilityHelp,
      Autoformat,
      AutoImage,
      Autosave,
      BlockQuote,
      RemoveFormat,
      Bold,
      Font,
      Link,
      Essentials,
      CodeBlock,
      Heading,
      ImageBlock,
      ImageCaption,
      ImageInline,
      ImageInsert,
      ImageInsertViaUrl,
      ImageStyle,
      ImageTextAlternative,
      ImageToolbar,
      ImageUpload,
      Indent,
      IndentBlock,
      Italic,
      List,
      ListProperties,
      HorizontalLine,
      CustomVideo,
      PageBreak,
      Paragraph,
      PictureEditing,
      SelectAll,
      Table,
      TableCaption,
      Strikethrough,
      Subscript,
      Superscript,
      TableCellProperties,
      TableColumnResize,
      TableProperties,
      TableToolbar,
      TextTransformation,
      TodoList,
      GeneralHtmlSupport,
      Underline,
      Undo,
      SimpleBox,
      Alignment,
      HtmlEmbed,
      SimpleBoxToolbar,
      InteractionBuilder,
    ],
    htmlEmbed: {
      showPreviews: true,
      sanitizeHtml: inputHtml => {
        return {
          html: inputHtml,
          hasChanged: false,
        }
      },
    },
    link: {
      addTargetToExternalLinks: false,
      defaultProtocol: 'https://',
      // Prevent link editing on button elements
      decorators: {
        isButton: {
          mode: 'manual',
          label: 'Button',
          attributes: {
            class: 'simple-button',
          },
        },
      },
    },
    language: language,
    fontData: fontFamily,
    htmlSupport: {
      allow: [
        {
          name: 'pre',
          attributes: true,
          classes: true,
        },
        {
          name: 'p',
          attributes: true,
          classes: true,
        },
        {
          name: 'ul',
          attributes: true,
          classes: true,
        },
        {
          name: 'ol',
          attributes: true,
          classes: true,
        },
        {
          name: 'span',
          attributes: true,
          classes: true,
        },
        {
          name: 'h1',
          attributes: true,
          classes: true,
        },
        {
          name: 'h2',
          attributes: true,
          classes: true,
        },
        {
          name: 'h3',
          attributes: true,
          classes: true,
        },
        {
          name: 'h4',
          attributes: true,
          classes: true,
        },
        {
          name: 'a',
          attributes: true,
          classes: true,
        },
        {
          name: 'code',
          attributes: true,
          classes: true,
        },
      ],
    },
    heading: {
      options: [
        {
          model: 'paragraph',
          title: 'Paragraph',
          class: 'ck-heading_paragraph',
        },
        {
          model: 'heading1',
          view: 'h1',
          title: 'Heading 1',
          class: 'ck-heading_heading1',
        },
        {
          model: 'heading2',
          view: 'h2',
          title: 'Heading 2',
          class: 'ck-heading_heading2',
        },
        {
          model: 'heading3',
          view: 'h3',
          title: 'Heading 3',
          class: 'ck-heading_heading3',
        },
      ],
    },
    simpleBox: {
      landingPageData: landingPageData || {},
      theme: theme,
      subPage: subPage,
      page: page,
      pageUrl: pageUrl,
      courseCards: courseCards || [],
      course: course || {},
      collection: localCollectionData?.collection || {}, // Use local state
      collectionCards: localCollectionData?.courses || [], // Use local state
      onAddButtonClick: position => {
        setIsOpen(true)
      },
    },
    image: {
      toolbar: ['imageTextAlternative', 'imageGalleryAi', 'imageGallery'],
    },
    video: {
      toolbar: ['aspectRatio', 'videoGalleryLink', 'videoGalleryChange'],
    },

    initialData: initialData
      ? initialData
      : `  
              <div class="simple-box-wrapper">
                <div class="simple-box">
                 <div class="column">
                     <p>I'm a paragraph. Click to start writing that will inform, inspire, or encourage your community. We know you have so many things to share with the world. Happy editing!</p>
                 </div>
               </div>
              </div>  
        `,
    enterMode: InlineEditor.ENTER_BR,
    licenseKey: ENV.LICENSE_KEY,
    list: {
      properties: {
        styles: true,
        startIndex: true,
        reversed: true,
      },
    },
    mention: {
      feeds: [
        {
          marker: '@',
          feed: [
            /* See: https://ckeditor.com/docs/ckeditor5/latest/features/mentions.html */
          ],
        },
      ],
    },
    placeholder: 'Type or paste your content here!',
    presenceList: {
      container: editorPresenceRef.current,
    },
    revisionHistory: {
      editorContainer: editorContainerRef.current,
      viewerContainer: editorRevisionHistoryRef.current,
      viewerEditorElement: editorRevisionHistoryEditorRef.current,
      viewerSidebarContainer: editorRevisionHistorySidebarRef.current,
      resumeUnsavedRevision: true,
    },
    sidebar: {
      container: editorAnnotationsRef.current,
    },
    table: {
      contentToolbar: [
        'tableColumn',
        'tableRow',
        'mergeTableCells',
        'tableProperties',
        'tableCellProperties',
      ],
    },
  }

  // Usage in your component:
  useEffect(() => {
    if (doubleClickData && editorRef.current) {
      const { data, type } = doubleClickData
      const editor = editorRef.current

      replacedropAreaWideContent(
        editor,
        data,
        type,
        dispatch,
        Embed,
        fontFamily,
        language,
      )
      setDoubleClickData(null)
    }
  }, [doubleClickData])

  if (pageUrl.includes('landing-page') && !isDataLoaded) {
    return null
  }
  if (!isDataLoaded) {
    return null
  }

  return (
    <>
      <Helmet>
        <link
          rel="stylesheet"
          href={`https://fonts.googleapis.com/css?family=${fontFamily}:wght@100;200;300;400;500;600;700;800;900&display=swap`}
        />
      </Helmet>

      <div className="main-container">
        <div className="presence" ref={editorPresenceRef}></div>
        <div
          className="editor-container editor-container_inline-editor editor-container_include-annotations editor-container_include-block-toolbar"
          ref={editorContainerRef}
        >
          <div className="editor-container__editor-wrapper">
            <div className="editor-container__editor">
              {isLayoutReady && editorConfig && (
                <div className={classes.ckEditorFont}>
                  <CKEditor
                    fontSize={fontSize}
                    fontFamily={fontFamily}
                    theme={theme}
                    page={page}
                    editor={InlineEditor}
                    onChange={!isReadOnly ? handleEditorChange : undefined}
                    onReady={editor => {
                      const editorWrapper = document.querySelector(
                        '.editor-container__editor',
                      )
                      const editorPointer = document.querySelector(
                        '.editor-container__editor-wrapper',
                      )
                      editor.model.document.selection.on('change:range', () => {
                        // Check if selected element is editorOutline
                        const selectedElement = editor.model.document.selection.getSelectedElement()
                        // Get the toolbar element
                        const toolbar = editor.ui.view.toolbar
                        const header = document.getElementsByClassName(
                          'editor-header',
                        )[0]
                        if (header) {
                          header.addEventListener('click', () => {
                            if (toolbar) {
                              toolbar.isEnabled = false

                              if (toolbar.element) {
                                toolbar.element.style.display = 'none'
                              }
                              handleHeaderSettingsModal(dispatch, pageUrl)
                            }
                          })
                        }

                        if (
                          selectedElement &&
                          selectedElement.name === 'editorOutline'
                        ) {
                          if (toolbar) {
                            toolbar.isEnabled = false
                            if (toolbar.element) {
                              toolbar.element.style.display = 'none'
                            }
                          }

                          // Also disable any balloon toolbars or panels
                          const balloons = Array.from(
                            document.querySelectorAll('.ck-balloon-panel'),
                          )

                          balloons.forEach(balloon => {
                            balloon.style.display = 'none'
                          })
                        } else {
                          // Re-enable the toolbar for other elements
                          if (toolbar) {
                            toolbar.isEnabled = true

                            if (toolbar.element) {
                              toolbar.element.style.display = ''
                            }
                          }
                        }
                      })
                      if (pageUrl === 'mini-course') {
                        if (subPage === 'editor')
                          editor.model.change(writer => {
                            const root = editor.model.document.getRoot()
                            const editorOutline = writer.createElement(
                              'editorOutline',
                            )

                            writer.append(editorOutline, root)

                            editor.editing.view.forceRender()
                          })
                        editor.model.change(writer => {
                          const root = editor.model.document.getRoot()
                          const editorHeader = writer.createElement(
                            'editorHeader',
                          )
                          writer.insert(editorHeader, root, 0)
                        })
                      } else {
                        if (subPage === 'editor')
                          editor.model.change(writer => {
                            const root = editor.model.document.getRoot()
                            const editorOutline = writer.createElement(
                              'editorOutline',
                            )

                            writer.append(editorOutline, root)

                            editor.editing.view.forceRender()
                          })
                        editor.model.change(writer => {
                          const root = editor.model.document.getRoot()
                          const editorHeader = writer.createElement(
                            'editorHeader',
                          )
                          writer.insert(editorHeader, root, 0)
                        })
                      }

                      if (isReadOnly) {
                        editorPointer.style.pointerEvents = 'none'
                        editorWrapper.style.pointerEvents = 'none'
                      }
                      handleEditorReady(
                        editorRef,
                        onDropClose,
                        dispatch,
                        Embed,
                      )(editor)
                    }}
                    config={editorConfig}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default withStyles(styles)(CKEditorComponent)
